import { useT, type Lang } from "@/lib/i18n";

type Dict = Record<string, string>;

const D: Record<Lang, Dict> = {
  cs: {
    title: "Můj profil",
    subtitle: "Vyplňte informace, které se zobrazí na vašem veřejném profilu.",
    fullName: "Jméno",
    headline: "Podtitulek",
    headlinePh: "Např. Sládek, milovník piva, majitel podniku…",
    bio: "O mně",
    bioPh: "Napište pár vět o sobě…",
    city: "Město",
    country: "Země",
    website: "Web",
    publicProfile: "Veřejný profil",
    publicProfileHint: "Když vypnete, profil se ostatním nezobrazí.",
    save: "Uložit profil",
    saved: "Profil uložen",
    saveError: "Profil se nepodařilo uložit: {msg}",
    loading: "Načítám profil…",
    aboutTitle: "O uživateli",
    noInfo: "Uživatel zatím nevyplnil žádné informace.",
    memberSince: "Členem od {date}",
    vacantsTitle: "Vacanty",
    vacantsCount: "Vlastněné Vacanty: {count}",
    vakantOwner: "Majitel Vacantů",
    editProfile: "Upravit profil",
    viewPublic: "Zobrazit veřejný profil",
    signInRequired: "Pro úpravu profilu se prosím přihlaste.",
    private: "Tento profil je soukromý.",
  },
  sk: {
    title: "Môj profil",
    subtitle: "Vyplňte informácie, ktoré sa zobrazia na vašom verejnom profile.",
    fullName: "Meno",
    headline: "Podtitulok",
    headlinePh: "Napr. Sládok, milovník piva, majiteľ podniku…",
    bio: "O mne",
    bioPh: "Napíšte pár viet o sebe…",
    city: "Mesto",
    country: "Krajina",
    website: "Web",
    publicProfile: "Verejný profil",
    publicProfileHint: "Keď vypnete, profil sa ostatným nezobrazí.",
    save: "Uložiť profil",
    saved: "Profil uložený",
    saveError: "Profil sa nepodarilo uložiť: {msg}",
    loading: "Načítavam profil…",
    aboutTitle: "O používateľovi",
    noInfo: "Používateľ zatiaľ nevyplnil žiadne informácie.",
    memberSince: "Členom od {date}",
    vacantsTitle: "Vacanty",
    vacantsCount: "Vlastnené Vacanty: {count}",
    vakantOwner: "Majiteľ Vacantov",
    editProfile: "Upraviť profil",
    viewPublic: "Zobraziť verejný profil",
    signInRequired: "Na úpravu profilu sa prosím prihláste.",
    private: "Tento profil je súkromný.",
  },
  en: {
    title: "My profile",
    subtitle: "Fill in the information shown on your public profile.",
    fullName: "Name",
    headline: "Headline",
    headlinePh: "E.g. Brewer, beer lover, venue owner…",
    bio: "About me",
    bioPh: "Write a few sentences about yourself…",
    city: "City",
    country: "Country",
    website: "Website",
    publicProfile: "Public profile",
    publicProfileHint: "When turned off, your profile is hidden from others.",
    save: "Save profile",
    saved: "Profile saved",
    saveError: "Could not save the profile: {msg}",
    loading: "Loading profile…",
    aboutTitle: "About the user",
    noInfo: "This user has not added any information yet.",
    memberSince: "Member since {date}",
    vacantsTitle: "Vacants",
    vacantsCount: "Vacants owned: {count}",
    vakantOwner: "Vacant owner",
    editProfile: "Edit profile",
    viewPublic: "View public profile",
    signInRequired: "Please sign in to edit your profile.",
    private: "This profile is private.",
  },
  de: {
    title: "Mein Profil",
    subtitle: "Geben Sie die Informationen ein, die in Ihrem öffentlichen Profil erscheinen.",
    fullName: "Name",
    headline: "Kurzbeschreibung",
    headlinePh: "z. B. Braumeister, Bierliebhaber, Lokalbesitzer…",
    bio: "Über mich",
    bioPh: "Schreiben Sie ein paar Sätze über sich…",
    city: "Stadt",
    country: "Land",
    website: "Webseite",
    publicProfile: "Öffentliches Profil",
    publicProfileHint: "Wenn deaktiviert, ist Ihr Profil für andere nicht sichtbar.",
    save: "Profil speichern",
    saved: "Profil gespeichert",
    saveError: "Profil konnte nicht gespeichert werden: {msg}",
    loading: "Profil wird geladen…",
    aboutTitle: "Über den Nutzer",
    noInfo: "Dieser Nutzer hat noch keine Informationen hinzugefügt.",
    memberSince: "Mitglied seit {date}",
    vacantsTitle: "Vacants",
    vacantsCount: "Besessene Vacants: {count}",
    vakantOwner: "Vacant-Besitzer",
    editProfile: "Profil bearbeiten",
    viewPublic: "Öffentliches Profil ansehen",
    signInRequired: "Bitte melden Sie sich an, um Ihr Profil zu bearbeiten.",
    private: "Dieses Profil ist privat.",
  },
  es: {
    title: "Mi perfil",
    subtitle: "Rellena la información que se mostrará en tu perfil público.",
    fullName: "Nombre",
    headline: "Titular",
    headlinePh: "Ej. Maestro cervecero, amante de la cerveza, propietario…",
    bio: "Sobre mí",
    bioPh: "Escribe unas frases sobre ti…",
    city: "Ciudad",
    country: "País",
    website: "Sitio web",
    publicProfile: "Perfil público",
    publicProfileHint: "Si lo desactivas, tu perfil no será visible para los demás.",
    save: "Guardar perfil",
    saved: "Perfil guardado",
    saveError: "No se pudo guardar el perfil: {msg}",
    loading: "Cargando perfil…",
    aboutTitle: "Sobre el usuario",
    noInfo: "Este usuario aún no ha añadido información.",
    memberSince: "Miembro desde {date}",
    vacantsTitle: "Vacants",
    vacantsCount: "Vacants en propiedad: {count}",
    vakantOwner: "Propietario de Vacants",
    editProfile: "Editar perfil",
    viewPublic: "Ver perfil público",
    signInRequired: "Inicia sesión para editar tu perfil.",
    private: "Este perfil es privado.",
  },
  fr: {
    title: "Mon profil",
    subtitle: "Renseignez les informations affichées sur votre profil public.",
    fullName: "Nom",
    headline: "Accroche",
    headlinePh: "Ex. Brasseur, amateur de bière, propriétaire…",
    bio: "À propos de moi",
    bioPh: "Écrivez quelques phrases sur vous…",
    city: "Ville",
    country: "Pays",
    website: "Site web",
    publicProfile: "Profil public",
    publicProfileHint: "Désactivé, votre profil n'est pas visible par les autres.",
    save: "Enregistrer le profil",
    saved: "Profil enregistré",
    saveError: "Impossible d'enregistrer le profil : {msg}",
    loading: "Chargement du profil…",
    aboutTitle: "À propos de l'utilisateur",
    noInfo: "Cet utilisateur n'a pas encore ajouté d'informations.",
    memberSince: "Membre depuis {date}",
    vacantsTitle: "Vacants",
    vacantsCount: "Vacants détenus : {count}",
    vakantOwner: "Propriétaire de Vacants",
    editProfile: "Modifier le profil",
    viewPublic: "Voir le profil public",
    signInRequired: "Connectez-vous pour modifier votre profil.",
    private: "Ce profil est privé.",
  },
  pt: {
    title: "O meu perfil",
    subtitle: "Preencha as informações mostradas no seu perfil público.",
    fullName: "Nome",
    headline: "Subtítulo",
    headlinePh: "Ex. Mestre cervejeiro, amante de cerveja, proprietário…",
    bio: "Sobre mim",
    bioPh: "Escreva algumas frases sobre si…",
    city: "Cidade",
    country: "País",
    website: "Website",
    publicProfile: "Perfil público",
    publicProfileHint: "Se desativar, o seu perfil não fica visível para os outros.",
    save: "Guardar perfil",
    saved: "Perfil guardado",
    saveError: "Não foi possível guardar o perfil: {msg}",
    loading: "A carregar perfil…",
    aboutTitle: "Sobre o utilizador",
    noInfo: "Este utilizador ainda não adicionou informações.",
    memberSince: "Membro desde {date}",
    vacantsTitle: "Vacants",
    vacantsCount: "Vacants detidos: {count}",
    vakantOwner: "Proprietário de Vacants",
    editProfile: "Editar perfil",
    viewPublic: "Ver perfil público",
    signInRequired: "Inicie sessão para editar o seu perfil.",
    private: "Este perfil é privado.",
  },
  pl: {
    title: "Mój profil",
    subtitle: "Uzupełnij informacje, które pojawią się na Twoim profilu publicznym.",
    fullName: "Imię i nazwisko",
    headline: "Nagłówek",
    headlinePh: "Np. Piwowar, miłośnik piwa, właściciel lokalu…",
    bio: "O mnie",
    bioPh: "Napisz kilka zdań o sobie…",
    city: "Miasto",
    country: "Kraj",
    website: "Strona www",
    publicProfile: "Profil publiczny",
    publicProfileHint: "Po wyłączeniu profil nie będzie widoczny dla innych.",
    save: "Zapisz profil",
    saved: "Profil zapisany",
    saveError: "Nie udało się zapisać profilu: {msg}",
    loading: "Wczytywanie profilu…",
    aboutTitle: "O użytkowniku",
    noInfo: "Ten użytkownik nie dodał jeszcze żadnych informacji.",
    memberSince: "Członek od {date}",
    vacantsTitle: "Vacanty",
    vacantsCount: "Posiadane Vacanty: {count}",
    vakantOwner: "Właściciel Vacantów",
    editProfile: "Edytuj profil",
    viewPublic: "Zobacz profil publiczny",
    signInRequired: "Zaloguj się, aby edytować profil.",
    private: "Ten profil jest prywatny.",
  },
  hr: {
    title: "Moj profil",
    subtitle: "Ispunite podatke koji će se prikazati na vašem javnom profilu.",
    fullName: "Ime",
    headline: "Podnaslov",
    headlinePh: "Npr. Pivar, ljubitelj piva, vlasnik lokala…",
    bio: "O meni",
    bioPh: "Napišite nekoliko rečenica o sebi…",
    city: "Grad",
    country: "Država",
    website: "Web stranica",
    publicProfile: "Javni profil",
    publicProfileHint: "Ako isključite, profil neće biti vidljiv drugima.",
    save: "Spremi profil",
    saved: "Profil spremljen",
    saveError: "Profil nije moguće spremiti: {msg}",
    loading: "Učitavanje profila…",
    aboutTitle: "O korisniku",
    noInfo: "Ovaj korisnik još nije dodao podatke.",
    memberSince: "Član od {date}",
    vacantsTitle: "Vacanti",
    vacantsCount: "Vacanti u vlasništvu: {count}",
    vakantOwner: "Vlasnik Vacanata",
    editProfile: "Uredi profil",
    viewPublic: "Pogledaj javni profil",
    signInRequired: "Prijavite se za uređivanje profila.",
    private: "Ovaj profil je privatan.",
  },
  ro: {
    title: "Profilul meu",
    subtitle: "Completați informațiile afișate pe profilul dvs. public.",
    fullName: "Nume",
    headline: "Subtitlu",
    headlinePh: "Ex. Berar, iubitor de bere, proprietar de local…",
    bio: "Despre mine",
    bioPh: "Scrieți câteva propoziții despre dvs…",
    city: "Oraș",
    country: "Țară",
    website: "Site web",
    publicProfile: "Profil public",
    publicProfileHint: "Dacă dezactivați, profilul nu va fi vizibil altora.",
    save: "Salvează profilul",
    saved: "Profil salvat",
    saveError: "Profilul nu a putut fi salvat: {msg}",
    loading: "Se încarcă profilul…",
    aboutTitle: "Despre utilizator",
    noInfo: "Acest utilizator nu a adăugat încă informații.",
    memberSince: "Membru din {date}",
    vacantsTitle: "Vacants",
    vacantsCount: "Vacants deținute: {count}",
    vakantOwner: "Proprietar de Vacants",
    editProfile: "Editează profilul",
    viewPublic: "Vezi profilul public",
    signInRequired: "Autentificați-vă pentru a edita profilul.",
    private: "Acest profil este privat.",
  },
  it: {
    title: "Il mio profilo",
    subtitle: "Inserisci le informazioni mostrate sul tuo profilo pubblico.",
    fullName: "Nome",
    headline: "Sottotitolo",
    headlinePh: "Es. Birraio, amante della birra, titolare di locale…",
    bio: "Su di me",
    bioPh: "Scrivi qualche frase su di te…",
    city: "Città",
    country: "Paese",
    website: "Sito web",
    publicProfile: "Profilo pubblico",
    publicProfileHint: "Se disattivato, il profilo non sarà visibile agli altri.",
    save: "Salva profilo",
    saved: "Profilo salvato",
    saveError: "Impossibile salvare il profilo: {msg}",
    loading: "Caricamento del profilo…",
    aboutTitle: "Informazioni sull'utente",
    noInfo: "Questo utente non ha ancora aggiunto informazioni.",
    memberSince: "Membro dal {date}",
    vacantsTitle: "Vacants",
    vacantsCount: "Vacants posseduti: {count}",
    vakantOwner: "Proprietario di Vacants",
    editProfile: "Modifica profilo",
    viewPublic: "Vedi profilo pubblico",
    signInRequired: "Accedi per modificare il tuo profilo.",
    private: "Questo profilo è privato.",
  },
  hu: {
    title: "Profilom",
    subtitle: "Töltse ki a nyilvános profilján megjelenő adatokat.",
    fullName: "Név",
    headline: "Alcím",
    headlinePh: "Pl. Sörfőző, sörrajongó, vendéglős…",
    bio: "Rólam",
    bioPh: "Írjon pár mondatot magáról…",
    city: "Város",
    country: "Ország",
    website: "Weboldal",
    publicProfile: "Nyilvános profil",
    publicProfileHint: "Kikapcsolva a profil nem látható mások számára.",
    save: "Profil mentése",
    saved: "Profil elmentve",
    saveError: "A profilt nem sikerült menteni: {msg}",
    loading: "Profil betöltése…",
    aboutTitle: "A felhasználóról",
    noInfo: "Ez a felhasználó még nem adott meg információt.",
    memberSince: "Tag {date} óta",
    vacantsTitle: "Vacantok",
    vacantsCount: "Birtokolt Vacantok: {count}",
    vakantOwner: "Vacant-tulajdonos",
    editProfile: "Profil szerkesztése",
    viewPublic: "Nyilvános profil megtekintése",
    signInRequired: "A profil szerkesztéséhez jelentkezzen be.",
    private: "Ez a profil privát.",
  },
  bg: {
    title: "Моят профил",
    subtitle: "Попълнете информацията, показвана в публичния ви профил.",
    fullName: "Име",
    headline: "Подзаглавие",
    headlinePh: "Напр. Пивовар, любител на бира, собственик на заведение…",
    bio: "За мен",
    bioPh: "Напишете няколко изречения за себе си…",
    city: "Град",
    country: "Държава",
    website: "Уебсайт",
    publicProfile: "Публичен профил",
    publicProfileHint: "Ако изключите, профилът няма да е видим за другите.",
    save: "Запази профила",
    saved: "Профилът е запазен",
    saveError: "Профилът не можа да бъде запазен: {msg}",
    loading: "Зареждане на профила…",
    aboutTitle: "За потребителя",
    noInfo: "Този потребител още не е добавил информация.",
    memberSince: "Член от {date}",
    vacantsTitle: "Ваканти",
    vacantsCount: "Притежавани Ваканти: {count}",
    vakantOwner: "Собственик на Ваканти",
    editProfile: "Редактирай профила",
    viewPublic: "Виж публичния профил",
    signInRequired: "Влезте, за да редактирате профила си.",
    private: "Този профил е частен.",
  },
};

export function useProfileT() {
  const { lang } = useT();
  return (key: string, vars?: Record<string, string | number>) => {
    const dict = D[lang] ?? D.en;
    let s = dict[key] ?? D.en[key] ?? key;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v));
    }
    return s;
  };
}
